import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { summaryCardTheme as t } from "./styles";

const formatServiceName = (name) =>
  name
    ? name.split(/[-_]/).map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(" ")
    : "";

function SummaryComplianceChart({ overall, services }) {
  const data = useMemo(() => {
    const list = Array.isArray(services) ? services : [];
    return list
      .filter((row) => (row.compliance_failed_controls_total ?? 0) > 0)
      .slice(0, 8)
      .map((row) => ({
        name: formatServiceName(row.service) || row.service || "—",
        value: row.compliance_failed_controls_total ?? 0,
        fill: t.colors.chart[1],
      }))
      .sort((a, b) => b.value - a.value);
  }, [services]);

  const totalControls = data.reduce((a, d) => a + d.value, 0);
  const totalArns = overall?.arns_compliance_failed ?? 0;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <Box sx={{ ...t.chartTooltip, fontFamily: t.fontFamily }}>
        <Typography component="span" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.75rem" }}>
          {d.name}
        </Typography>
        <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", ml: 0.5 }}>
          — {d.value} control findings
        </Typography>
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
          fontFamily: t.fontFamily,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No compliance control findings
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        fontFamily: t.fontFamily,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.875rem", mb: 1, fontFamily: t.fontFamily }}>
        Compliance
      </Typography>
      <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", mb: 2, fontFamily: t.fontFamily }}>
        {totalArns} ARNs with findings · {totalControls} total control findings
      </Typography>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.colors.borderLight} horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: t.colors.textMuted }} />
          <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 10, fill: t.colors.text }} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: t.colors.background }} />
          <Bar dataKey="value" nameKey="name" radius={[0, 4, 4, 0]} maxBarSize={24} fill={t.colors.chart[1]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default SummaryComplianceChart;
